/**
 * Outstanding by Project Drilldown Component
 * Phase 9.38 - Dashboard Stat Drilldowns
 *
 * Shows outstanding invoices broken down by project with overdue highlighting
 */
import { useOutstandingByProject } from '@/hooks/useDashboardReports';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateOnly } from '@/lib/utils';

type SortBy = 'amount' | 'overdue' | 'oldest' | 'client';

export function OutstandingByProject() {
  const { data, isLoading, error } = useOutstandingByProject();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortBy>('amount');

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load outstanding invoices data
      </div>
    );
  }

  const projects = data?.data || [];
  const summary = data?.summary || {
    total_outstanding: 0,
    total_overdue: 0,
    invoice_count: 0,
    project_count: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    // oldest_invoice_date is a DATE column. Parsing it with the bare Date
    // constructor treats it as UTC midnight, which renders one calendar
    // day early west of UTC (e.g. America/Edmonton). formatDateOnly parses
    // the y/m/d components directly so the displayed date always matches
    // the stored calendar date.
    if (!dateStr) return '-';
    return formatDateOnly(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Sort projects
  const sortedProjects = [...projects].sort((a, b) => {
    if (sortBy === 'amount') {
      return b.outstanding_amount - a.outstanding_amount;
    } else if (sortBy === 'overdue') {
      return b.overdue_amount - a.overdue_amount;
    } else if (sortBy === 'oldest') {
      const dateA = a.oldest_invoice_date ? new Date(a.oldest_invoice_date).getTime() : 0;
      const dateB = b.oldest_invoice_date ? new Date(b.oldest_invoice_date).getTime() : 0;
      return dateA - dateB;
    } else {
      return (a.client_names || '').localeCompare(b.client_names || '');
    }
  });

  const handleRowClick = (projectId: string) => {
    navigate(`/projects/${projectId}?section=invoices`);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="outstanding-by-project">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Outstanding by Project
        </h2>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => setSortBy('amount')}
            className={`px-3 py-1 rounded ${sortBy === 'amount' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Amount
          </button>
          <button
            onClick={() => setSortBy('overdue')}
            className={`px-3 py-1 rounded ${sortBy === 'overdue' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Overdue
          </button>
          <button
            onClick={() => setSortBy('oldest')}
            className={`px-3 py-1 rounded ${sortBy === 'oldest' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Oldest
          </button>
          <button
            onClick={() => setSortBy('client')}
            className={`px-3 py-1 rounded ${sortBy === 'client' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Client
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Outstanding</div>
          <div className="text-xl font-bold text-sky-600" data-testid="summary-total">
            {formatCurrency(summary.total_outstanding)}
          </div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Overdue</div>
          <div className="text-xl font-bold text-red-600" data-testid="summary-overdue">
            {formatCurrency(summary.total_overdue)}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Invoices</div>
          <div className="text-xl font-bold text-gray-900" data-testid="summary-invoices">
            {summary.invoice_count}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Projects</div>
          <div className="text-xl font-bold text-gray-900" data-testid="summary-projects">
            {summary.project_count}
          </div>
        </div>
      </div>

      {/* Table */}
      {sortedProjects.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Overdue</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Invoices</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Oldest</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedProjects.map((project) => (
                <tr
                  key={project.project_id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(project.project_id)}
                >
                  <td className="px-4 py-3 text-sm">
                    <span className="font-medium text-gray-900">
                      {project.project_number ? `#${project.project_number}` : 'Proposal'}
                    </span>
                    <span className="ml-2 text-gray-600">{project.project_name}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {project.client_names || 'No Client'}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-sky-600">
                    {formatCurrency(project.outstanding_amount)}
                  </td>
                  <td className={`px-4 py-3 text-sm font-semibold text-right ${project.overdue_amount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {formatCurrency(project.overdue_amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600">
                    {project.invoice_count}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(project.oldest_invoice_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No outstanding invoices found
        </div>
      )}
    </div>
  );
}
